/**
 * FilesToolbar Component
 * Toolbar for the files page with sidebar toggle, breadcrumb, search, sort, and upload
 */

import { html, attr, on, Signal, When, computedOf } from '@tempots/dom'
import { Button, ButtonVariant, Icon, TextInput } from '@tempots/beatui'
import { FolderBreadcrumb } from './breadcrumb'
import { UploadProgress } from './upload-progress'
import type { FolderResponse } from '@/services/types'

// ============================================================================
// Types
// ============================================================================

export interface FilesToolbarProps {
  /** Whether the sidebar is collapsed */
  sidebarCollapsed: Signal<boolean>
  /** Callback to toggle sidebar */
  onToggleSidebar: () => void
  /** Folder path for breadcrumb */
  folderPath: Signal<FolderResponse[]>
  /** Callback when navigating via breadcrumb */
  onNavigate: (folderId: string) => void
  /** Current search query */
  searchQuery: Signal<string>
  /** Callback when search changes */
  onSearchChange: (value: string) => void
  /** Whether to include subfolders in search */
  includeSubfolders: Signal<boolean>
  /** Callback when include subfolders changes */
  onIncludeSubfoldersChange: (checked: boolean) => void
  /** Current sort field */
  sortBy: Signal<'filename' | 'size' | 'createdAt'>
  /** Callback when sort field changes */
  onSortByChange: (field: 'filename' | 'size' | 'createdAt') => void
  /** Current sort order */
  sortOrder: Signal<'asc' | 'desc'>
  /** Callback to toggle sort order */
  onToggleSortOrder: () => void
  /** Whether upload is in progress */
  uploading: Signal<boolean>
  /** Upload progress (0-100) */
  uploadProgress: Signal<number>
  /** Total files being uploaded */
  uploadingCount: Signal<number>
  /** Files already uploaded */
  uploadedCount: Signal<number>
  /** Callback to trigger file input */
  onUploadClick: () => void
}

// ============================================================================
// Component
// ============================================================================

/**
 * FilesToolbar - Toolbar for the files page
 *
 * @example
 * ```typescript
 * FilesToolbar({
 *   sidebarCollapsed,
 *   onToggleSidebar: () => sidebarCollapsed.set(!sidebarCollapsed.get()),
 *   folderPath,
 *   onNavigate: handleFolderSelect,
 *   searchQuery,
 *   onSearchChange: handleSearchChange,
 *   includeSubfolders,
 *   onIncludeSubfoldersChange: handleIncludeSubfoldersChange,
 *   sortBy,
 *   onSortByChange: (field) => { sortBy.set(field); loadFiles() },
 *   sortOrder,
 *   onToggleSortOrder: () => { sortOrder.set(sortOrder.get() === 'asc' ? 'desc' : 'asc'); loadFiles() },
 *   uploading,
 *   uploadProgress,
 *   uploadingCount,
 *   uploadedCount,
 *   onUploadClick: triggerFileInput,
 * })
 * ```
 */
export function FilesToolbar({
  sidebarCollapsed,
  onToggleSidebar,
  folderPath,
  onNavigate,
  searchQuery,
  onSearchChange,
  includeSubfolders,
  onIncludeSubfoldersChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onToggleSortOrder,
  uploading,
  uploadProgress,
  uploadingCount,
  uploadedCount,
  onUploadClick,
}: FilesToolbarProps) {
  const hasFolderPath = folderPath.map((p) => p.length > 0)
  const hasSearchQuery = searchQuery.map((q) => q.trim().length > 0)

  return html.div(
    attr.class(
      'flex items-center gap-4 px-6 py-3 border-b border-gray-200 bg-white'
    ),
    // Toggle sidebar button
    Button(
      {
        variant: 'ghost' as ButtonVariant,
        size: 'sm',
        onClick: onToggleSidebar,
      },
      attr.title(
        sidebarCollapsed.map((c) => (c ? 'Show sidebar' : 'Hide sidebar'))
      ),
      Icon(
        {
          icon: sidebarCollapsed.map((c): string =>
            c ? 'mdi:menu' : 'mdi:menu-open'
          ),
        },
        attr.class('w-5 h-5')
      )
    ),
    // Breadcrumb
    html.div(
      attr.class('flex-1 min-w-0'),
      When(
        hasFolderPath,
        () =>
          FolderBreadcrumb({
            path: folderPath,
            onNavigate,
          }),
        () => html.span(attr.class('text-sm text-gray-600'), 'All Files')
      )
    ),
    // Sort controls and Search
    html.div(
      attr.class('flex items-center gap-3'),
      // Sort by dropdown
      html.select(
        attr.class(
          'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
        ),
        attr.value(sortBy),
        on.change((e) => {
          onSortByChange(
            (e.target as HTMLSelectElement).value as
              | 'filename'
              | 'size'
              | 'createdAt'
          )
        }),
        html.option(attr.value('filename'), 'Name'),
        html.option(attr.value('size'), 'Size'),
        html.option(attr.value('createdAt'), 'Date')
      ),
      // Sort order toggle
      Button(
        {
          variant: 'ghost' as ButtonVariant,
          size: 'sm',
          onClick: onToggleSortOrder,
        },
        attr.title(
          sortOrder.map((order) =>
            order === 'asc' ? 'Ascending' : 'Descending'
          )
        ),
        Icon(
          {
            icon: sortOrder.map((order): string =>
              order === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending'
            ),
          },
          attr.class('w-5 h-5')
        )
      ),
      html.div(
        attr.class('w-64'),
        TextInput({
          value: searchQuery,
          placeholder: 'Search files...',
          before: Icon(
            { icon: 'mdi:magnify' },
            attr.class('w-5 h-5 text-gray-400 pl-2')
          ),
          onChange: onSearchChange,
        })
      ),
      // Include subfolders checkbox (only show when in a folder or searching)
      When(
        computedOf(
          hasFolderPath,
          hasSearchQuery
        )((hasPath, hasSearch) => hasPath || hasSearch),
        () =>
          html.label(
            attr.class(
              'flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap'
            ),
            html.input(
              attr.type('checkbox'),
              attr.class(
                'w-4 h-4 text-brand-600 bg-gray-100 border-gray-300 rounded focus:ring-brand-500 cursor-pointer'
              ),
              attr.checked(includeSubfolders),
              on.change((e) => {
                const checkbox = e.target as HTMLInputElement
                onIncludeSubfoldersChange(checkbox.checked)
              })
            ),
            'Include subfolders'
          )
      )
    ),
    // Upload button and progress
    html.div(
      attr.class('flex items-center gap-3'),
      // Upload progress indicator
      When(uploading, () =>
        UploadProgress({
          progress: uploadProgress,
          totalCount: uploadingCount,
          uploadedCount: uploadedCount,
        })
      ),
      // Upload button
      Button(
        {
          disabled: uploading,
          onClick: onUploadClick,
        },
        Icon({ icon: 'mdi:cloud-upload' }, attr.class('w-4 h-4 mr-2')),
        uploading.map((u) => (u ? 'Uploading...' : 'Upload'))
      )
    )
  )
}
